import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import './css/mainviewcategories.css';
import './css/branchcategories.css';
import FooterNote from './FooterNote';

const BranchCategories = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  
  const [branch, setBranch] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('display_order'); // 'name', 'display_order'

  useEffect(() => {
    fetchBranchData();
  }, [branchId, language]);

  const fetchBranchData = async () => {
    try {
      setLoading(true);
      
      // Fetch branch details
      const branchRes = await axios.get(`${API_URL}/api/branches/${branchId}`);
      const branchData = branchRes.data;
      
      if (!branchData || !branchData.is_active) {
        setLoading(false);
        return;
      }
      
      setBranch(branchData);
      
      // Get branch categories
      const branchCategories = branchData.categories || [];
      
      // Fetch all categories to get full details
      const categoriesRes = await axios.get(`${API_URL}/api/categories`);
      const allCategories = categoriesRes.data;
      
      // Filter and map branch categories
      const branchCategoryIds = branchCategories.map(bc => bc.id || bc);
      const filteredCategories = allCategories
        .filter(cat => branchCategoryIds.includes(cat.id) && cat.is_active)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .map(cat => ({
          id: cat.id,
          name: language === 'ar' ? cat.name_ar : cat.name_en,
          key: cat.key_name,
          image: cat.image ? getUploadUrl(cat.image) : null,
          link: `/branch/${branchId}/category/${cat.key_name}`,
          display_order: cat.display_order
        }));
      
      setCategories(filteredCategories);
    } catch (error) {
      console.error('Error fetching branch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort categories
  const filteredCategories = useMemo(() => {
    let filtered = [...categories];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(cat =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort categories
    if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'display_order') {
      filtered.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    }

    return filtered;
  }, [categories, searchTerm, sortBy]);

  if (loading) {
    return (
      <section className='main-view-categories'>
        <div className='container-fluid'>
          <div className='loading-message'>
            <p>{t('branches.loading') || 'Loading...'}</p>
          </div>
        </div>
        <FooterNote />
      </section>
    );
  }

  if (!branch) {
    return (
      <section className='main-view-categories'>
        <div className='container-fluid'>
          <div className='error-message'>
            <p>{t('branches.branchNotFound')}</p>
            <button onClick={() => navigate('/branches')}>{t('branches.backToBranches')}</button>
          </div>
        </div>
        <FooterNote />
      </section>
    );
  }

  const branchName = language === 'ar' ? branch.name_ar : branch.name_en;
  const branchLocation = language === 'ar' ? branch.location_ar : branch.location_en;

  return (
    <section className='main-view main-view-categories'>
      <div className='container-fluid'>
        <div className='main-view-categories-content'>
          {/* Header */}
          <div className='branch-categories-header'>
            <button 
              className='back-btn'
              onClick={() => navigate('/branches')}
            >
              {language === 'ar' ? '← ' : ''}{t('branches.backToBranches')}{language === 'en' ? ' ←' : ''}
            </button>
            <div>
              <h1 className='branch-categories-title'>{branchName}</h1>
              <p className='branch-info'>{branchLocation} {branch.phone && `- ${branch.phone}`}</p>
            </div>
          </div>

          <div className='categories-header'>
            <button 
              className='open-filter-btn'
              onClick={() => setShowFilter(!showFilter)}
            >
              <FunnelIcon className='icon' />
              <span>{t('categories.filter')}</span>
            </button>
          </div>

          {/* Filter Panel */}
          {showFilter && (
            <div className='filter-panel'>
              <div className='filter-header'>
                <h3>{t('categories.filter')}</h3>
                <button 
                  className='close-filter-btn'
                  onClick={() => setShowFilter(false)}
                >
                  <XMarkIcon className='icon' />
                </button>
              </div>
              
              <div className='filter-content'>
                <div className='filter-group'>
                  <label>{t('categories.search')}</label>
                  <input
                    type='text'
                    placeholder={t('categories.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className='filter-input'
                  />
                </div>

                <div className='filter-group'>
                  <label>{t('categories.sortBy') || 'Sort By'}</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className='filter-select'
                  >
                    <option value='name'>{t('categories.sortByName')}</option>
                    <option value='display_order'>{t('categories.sortByOriginal')}</option>
                  </select>
                </div>

                <div className='filter-results'>
                  <span>{t('categories.results')}: {filteredCategories.length}</span>
                </div>
              </div>
            </div>
          )}

          <div className="categories-list">
            <div className='row'>
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <div className='col-lg-6 col-md-6 col-6' key={category.id}>
                    <div 
                      className='category-item'
                      onClick={() => navigate(category.link)}
                      style={{ cursor: 'pointer' }}
                    >
                      {category.image ? (
                        <img 
                          src={category.image} 
                          alt={category.name} 
                          className='category-image' 
                        />
                      ) : (
                        <div className='category-image-placeholder'>{category.name}</div>
                      )}
                      <h3>{category.name}</h3>
                    </div>
                  </div>
                ))
              ) : (
                <div className='col-6'>
                  <div className='no-categories'>
                    <p>{t('branches.noCategories')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className='calories-disclaimer'>
            <p>{t('categories.disclaimer')}</p>
          </div>
        </div>
      </div>
      <FooterNote />
    </section>
  );
};

export default BranchCategories;
